import dayjs, { Dayjs } from 'dayjs';
import { PhoneNumberStart } from '../constants';
import ShortUniqueId from 'short-unique-id';
import { isEmpty, isNil, isBoolean } from 'lodash';

export const formatDateWithDayJs = (
  value: any,
  ignoreTime?: boolean,
  templateFormat = 'YYYY-MM-DD',
  timeFormat = 'HH:mm:ss',
) => {
  const template = ignoreTime
    ? templateFormat
    : templateFormat + ' ' + timeFormat;

  return value ? dayjs(new Date(value)).format(template) : '';
};

export const formatDateOnly = (value: any, templateFormat = 'YYYY-MM-DD') => {
  return value ? dayjs(new Date(value)).format(templateFormat) : '';
};

export const convertFirstLetterToUppercase = (value: string) => {
  const firstLetter = value.charAt(0).toUpperCase();

  const remainedLetter = value.substring(1);

  return `${firstLetter}${remainedLetter.toLocaleLowerCase()}`;
};

export const formatNumber = (value: number) => {
  return value < 9 ? `0${value}` : `${value}`;
};

export const toCapitalize = (value?: string) => {
  // make first letter capitalize
  if (value) {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }
};

export const checkValidPhoneNumber = (value: string) => {
  return (
    value.startsWith(PhoneNumberStart['072']) ||
    value.startsWith(PhoneNumberStart['073']) ||
    value.startsWith(PhoneNumberStart['078']) ||
    value.startsWith(PhoneNumberStart['079']) ||
    value.startsWith(PhoneNumberStart['72']) ||
    value.startsWith(PhoneNumberStart['73']) ||
    value.startsWith(PhoneNumberStart['78']) ||
    value.startsWith(PhoneNumberStart['79']) ||
    value.startsWith(PhoneNumberStart['25072']) ||
    value.startsWith(PhoneNumberStart['25073']) ||
    value.startsWith(PhoneNumberStart['25078']) ||
    value.startsWith(PhoneNumberStart['25079'])
  );
};

export const checkMtnPhone = (value: string) => {
  return (
    value.startsWith(PhoneNumberStart['25078']) ||
    value.startsWith(PhoneNumberStart['25079']) ||
    value.startsWith(PhoneNumberStart['78']) ||
    value.startsWith(PhoneNumberStart['79']) ||
    value.startsWith(PhoneNumberStart['078']) ||
    value.startsWith(PhoneNumberStart['079'])
  );
};

export const checkAirtelPhone = (value: string) => {
  return (
    value.startsWith(PhoneNumberStart['25073']) ||
    value.startsWith(PhoneNumberStart['25072']) ||
    value.startsWith(PhoneNumberStart['72']) ||
    value.startsWith(PhoneNumberStart['73']) ||
    value.startsWith(PhoneNumberStart['073']) ||
    value.startsWith(PhoneNumberStart['072'])
  );
};

export const isDevelopment = process.env.NODE_ENV === 'development';

export const isProduction = process.env.NODE_ENV === 'production';

export const merge = (...rest: any) => Object.assign({}, ...rest);

/**
 *
 * @description generate unique id for JSX maps
 */
export const getUniqueId = () => {
  const uid = new ShortUniqueId({ length: 10 });

  const randomNumber = Date.now() + Number(String(Math.random()).split('.')[1]);

  return uid.randomUUID() + '-' + randomNumber;
};

export const isUrl = (value?: string) => {
  if (!value) {
    return false;
  }

  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
};

/**
 *
 * @param value
 * @description check if string contains special characters
 */
export const containSpecialCharacter = (value: string) => {
  // Regex to check if a string contains only special characters
  const regex = /[^a-zA-Z0-9]/;

  if (isEmpty(value)) {
    return false;
  }

  const isMatch = regex.test(value);

  return isMatch;
};

export const removeSpecialCharacters = (text: string) => {
  return text.replace(/[^a-zA-Z0-9 ]/g, '');
};

export const downloadFile = (file: Blob, fileName: string) => {
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(file);
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
};

export const getFileNameFromHeaders = (headers: any, defaultName: string) => {
  const fileName = headers['content-disposition']?.split('filename=')[1];
  if (fileName) {
    return fileName;
  }
  const contentType = headers['content-type'];
  const name = `${defaultName}-${dayjs().format('YYYY-MM-DD-HH-mm-ss')}`;
  if (contentType === 'application/pdf') {
    return `${name}.pdf`;
  }
  return `${name}.xlsx`;
};

export const getChange = (
  original?: string | boolean | number | null,
  change?: string | boolean | number | null,
  isDate?: boolean,
) => {
  if (original === change || isNil(change)) {
    return 'N/A';
  }
  return isDate
    ? formatDateWithDayJs(change, true, 'YYYY/MM/DD')
    : isBoolean(change)
      ? change
        ? 'Yes'
        : 'No'
      : change;
};

export const formatDateRange = (dateRange?: [Dayjs | null, Dayjs | null]) => {
  const [startDate, endDate] = dateRange || [null, null];
  return {
    ...(startDate && {
      startDate: dayjs(startDate).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
    }),
    ...(endDate && {
      endDate: dayjs(endDate).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
    }),
  };
};

export function getRelativeTimeString(date: string | Date | Dayjs): string {
  const messageDate = dayjs(date);
  const now = dayjs();
  const diffInDays = now.diff(messageDate, 'day');

  // If message is from today
  if (messageDate.isSame(now, 'day')) {
    return messageDate.format('HH:mm');
  }

  // If message is from yesterday
  if (messageDate.isSame(now.subtract(1, 'day'), 'day')) {
    return 'Yesterday';
  }

  // If message is from this week (within last 7 days)
  if (diffInDays < 7) {
    return messageDate.format('dddd'); // Returns full day name
  }

  // If message is older than a week
  return messageDate.format('YYYY/MM/DD');
}
